export interface Translations {
  pageTitle: string;
  pageDescription: string;
  headerLabels: Record<string, string>;
  totalAmount: string;
  cardNumber: string;
  cardHolder: string;
  cardHolderPlaceholder: string;
  expiry: string;
  expiryMonthPlaceholder: string;
  expiryYearPlaceholder: string;
  cvv: string;
  installments: string;
  installmentSingular: string;
  installmentPlural: string;
  of: string;
  pay: string;
  processing: string;
  sslSecure: string;
  quantity: string;
  successTitle: string;
  successMessage: string;
  successButton: string;
  refusedTitle: string;
  refusedMessage: string;
  errorCardRequired: string;
  errorCardInvalid: string;
  errorHolderRequired: string;
  errorHolderMinLength: string;
  errorExpiryRequired: string;
  errorExpiryExpired: string;
  errorCvvRequired: string;
  errorCvvDigits: (digits: number) => string;
  // RequiredInformation
  requiredInfoTitle: string;
  requiredFieldError: string;
  // WCAG / Accessibility
  ariaPaymentForm: string;
  ariaCardSection: string;
  ariaAmountSection: string;
  ariaProductsSection: string;
  aria3dsChallenge: string;
  ariaPaySuccess: string;
  ariaPayRefused: string;
}

const es: Translations = {
  pageTitle: "Formulario de Pago",
  pageDescription: "Formulario de pago seguro con tarjeta de crédito y débito",
  headerLabels: { pedido: "Pedido", orden: "Orden", operacion: "Operación" },
  totalAmount: "Importe total",
  cardNumber: "Número de tarjeta",
  cardHolder: "Titular",
  cardHolderPlaceholder: "NOMBRE APELLIDO",
  expiry: "Vencimiento",
  expiryMonthPlaceholder: "MM",
  expiryYearPlaceholder: "AA",
  cvv: "CVV",
  installments: "Cuotas",
  installmentSingular: "cuota",
  installmentPlural: "cuotas",
  of: "de",
  pay: "Pagar",
  processing: "Procesando...",
  sslSecure: "Pago seguro SSL",
  quantity: "Cant",
  successTitle: "¡Pago Exitoso!",
  successMessage: "Tu pago ha sido procesado correctamente.",
  successButton: "Realizar otro pago",
  refusedTitle: "Transacción rechazada",
  refusedMessage: "El pago no fue acreditado.",
  errorCardRequired: "El número de tarjeta es requerido",
  errorCardInvalid: "Número de tarjeta inválido",
  errorHolderRequired: "El nombre del titular es requerido",
  errorHolderMinLength: "El nombre debe tener al menos 3 caracteres",
  errorExpiryRequired: "La fecha de vencimiento es requerida",
  errorExpiryExpired: "La tarjeta está vencida",
  errorCvvRequired: "El código de seguridad es requerido",
  errorCvvDigits: (digits: number) => `El CVV debe tener ${digits} dígitos`,
  requiredInfoTitle: "Información requerida",
  requiredFieldError: "Este campo es obligatorio",
  ariaPaymentForm: "Formulario de pago seguro",
  ariaCardSection: "Datos de la tarjeta",
  ariaAmountSection: "Importe de la operación",
  ariaProductsSection: "Detalle de productos",
  aria3dsChallenge: "Verificación de seguridad bancaria",
  ariaPaySuccess: "Pago procesado exitosamente",
  ariaPayRefused: "Pago rechazado",
};

const en: Translations = {
  pageTitle: "Payment Form",
  pageDescription: "Secure credit and debit card payment form",
  headerLabels: { pedido: "Order", orden: "Order", operacion: "Transaction" },
  totalAmount: "Total amount",
  cardNumber: "Card number",
  cardHolder: "Cardholder",
  cardHolderPlaceholder: "FULL NAME",
  expiry: "Expiration",
  expiryMonthPlaceholder: "MM",
  expiryYearPlaceholder: "YY",
  cvv: "CVV",
  installments: "Installments",
  installmentSingular: "installment",
  installmentPlural: "installments",
  of: "of",
  pay: "Pay",
  processing: "Processing...",
  sslSecure: "SSL secure payment",
  quantity: "Qty",
  successTitle: "Payment Successful!",
  successMessage: "Your payment has been processed successfully.",
  successButton: "Make another payment",
  refusedTitle: "Transaction declined",
  refusedMessage: "The payment was not credited.",
  errorCardRequired: "Card number is required",
  errorCardInvalid: "Invalid card number",
  errorHolderRequired: "Cardholder name is required",
  errorHolderMinLength: "Name must be at least 3 characters",
  errorExpiryRequired: "Expiration date is required",
  errorExpiryExpired: "Card is expired",
  errorCvvRequired: "Security code is required",
  errorCvvDigits: (digits: number) => `CVV must be ${digits} digits`,
  requiredInfoTitle: "Required information",
  requiredFieldError: "This field is required",
  ariaPaymentForm: "Secure payment form",
  ariaCardSection: "Card details",
  ariaAmountSection: "Transaction amount",
  ariaProductsSection: "Product details",
  aria3dsChallenge: "Bank security verification",
  ariaPaySuccess: "Payment processed successfully",
  ariaPayRefused: "Payment declined",
};

const pt: Translations = {
  pageTitle: "Formulário de Pagamento",
  pageDescription: "Formulário de pagamento seguro com cartão de crédito e débito",
  headerLabels: { pedido: "Pedido", orden: "Ordem", operacion: "Operação" },
  totalAmount: "Valor total",
  cardNumber: "Número do cartão",
  cardHolder: "Titular",
  cardHolderPlaceholder: "NOME COMPLETO",
  expiry: "Validade",
  expiryMonthPlaceholder: "MM",
  expiryYearPlaceholder: "AA",
  cvv: "CVV",
  installments: "Parcelas",
  installmentSingular: "parcela",
  installmentPlural: "parcelas",
  of: "de",
  pay: "Pagar",
  processing: "Processando...",
  sslSecure: "Pagamento seguro SSL",
  quantity: "Qtd",
  successTitle: "Pagamento Realizado!",
  successMessage: "Seu pagamento foi processado com sucesso.",
  successButton: "Realizar outro pagamento",
  refusedTitle: "Transação recusada",
  refusedMessage: "O pagamento não foi creditado.",
  errorCardRequired: "O número do cartão é obrigatório",
  errorCardInvalid: "Número do cartão inválido",
  errorHolderRequired: "O nome do titular é obrigatório",
  errorHolderMinLength: "O nome deve ter pelo menos 3 caracteres",
  errorExpiryRequired: "A data de validade é obrigatória",
  errorExpiryExpired: "O cartão está vencido",
  errorCvvRequired: "O código de segurança é obrigatório",
  errorCvvDigits: (digits: number) => `O CVV deve ter ${digits} dígitos`,
  requiredInfoTitle: "Informações obrigatórias",
  requiredFieldError: "Este campo é obrigatório",
  ariaPaymentForm: "Formulário de pagamento seguro",
  ariaCardSection: "Dados do cartão",
  ariaAmountSection: "Valor da operação",
  ariaProductsSection: "Detalhes dos produtos",
  aria3dsChallenge: "Verificação de segurança bancária",
  ariaPaySuccess: "Pagamento processado com sucesso",
  ariaPayRefused: "Pagamento recusado",
};

const translationsMap: Record<string, Translations> = { es, en, pt };

export function getTranslations(locale: string): Translations {
  const lang = locale.split("-")[0].toLowerCase();
  return translationsMap[lang] ?? es;
}

export function getLangFromLocale(locale: string): string {
  const lang = locale.split("-")[0].toLowerCase();
  if (lang in translationsMap) return lang;
  return "es";
}
